import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, APIError } from '../../core/_axios';
import { TemplateCrop } from '../../core/_models';
import { managementGetCropByUniqueIdentifier } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import ManageUserForm from './ManageUserForm';
import ManageCropForm from './ManageCropForm';
import { usePageHeader } from '../../contexts/PageHeaderContext';

export function ManageCropRoutes() {
    const axios = useAxios();
    const { setPageHeader } = usePageHeader();

    const {
        pathname
    } = useLocation();

    const params = useParams();

    usePageTitle('Manage Crop');

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [crop, setCrop] = useState<TemplateCrop | null>(null);

    const loadCrop = useCallback(async () => {
        if (!params.cropUniqueId) {
            setLoading(false);
            setLoadingError('No crop unique identifier provided.');
            return;
        }
        setLoading(true);
        setLoadingError(null);
        try {
            const updatedCrop = await managementGetCropByUniqueIdentifier(axios, params.cropUniqueId);
            setCrop(updatedCrop);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, params.cropUniqueId]);

    // Set up page header
    useEffect(() => {
        setPageHeader('Manage Crop');
        return () => setPageHeader(null);
    }, [setPageHeader]);

    useEffect(() => {
        loadCrop();
    }, [loadCrop]);

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadCrop}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fixed">
            <div className='mb-5'>
                <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400">
                    <li className="me-2">
                        <Link to={`/management/crops/${crop.unique_identifier}`}
                            className={[
                                pathname === `/management/crops/${crop.unique_identifier}/details` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Details
                        </Link>
                    </li>
                    {false && <>
                        <li className="me-2">
                            <Link to={`/management/accounts/${crop.unique_identifier}/crops`}
                                className={[
                                    pathname === `/management/accounts/${crop.unique_identifier}/crops` ?
                                        'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                        'text-gray-500 dark:text-gray-400',
                                    'inline-block p-4'
                                ].join(' ')}>
                                Users
                            </Link>
                        </li>
                        <li className="me-2">
                            <Link to={`/management/accounts/${crop.unique_identifier}/add-user`}
                                className={[
                                    pathname === `/management/accounts/${crop.unique_identifier}/add-user` ?
                                        'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                        'text-gray-500 dark:text-gray-400',
                                    'inline-block p-4'
                                ].join(' ')}>
                                Add User
                            </Link>
                        </li>
                    </>}
                </ul>
            </div>
            <Routes>
                <Route path="/details" element={
                    <ManageCropForm
                        crop={crop}
                        setCrop={setCrop}
                    />}
                />
                <Route
                    path="*"
                    element={<Navigate
                        to={`/admin/manage-crops/${crop.unique_identifier}/details`}
                        replace
                    />}
                />
            </Routes>
        </div>
    );
}